import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { AlertPill } from '@/experiences/sales-portal/domain/insights/components/alert-card/alert-pill';
import { AlertSupportInfo } from '@/experiences/sales-portal/domain/insights/components/alert-card/alert-support-info/alert-support-info';
import {
  Avatar,
  createAvatarProps,
  DropdownMenu,
  Icons,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { GestureResponderEvent, Platform, Pressable } from 'react-native';

// TODO(SRG-339): remove the IS_MOBILE conditional values once SKUI resolves
// tokens per breakpoint.
const IS_MOBILE = Platform.OS !== 'web';

const ELLIPSIS_ICON_SIZE = IS_MOBILE ? '$20' : '$24';
const MENU_MIN_WIDTH = 160;

export interface AlertCardAction {
  key: string;
  label: string;
  onPress: () => void;
}

export interface AlertCardProps {
  actions: AlertCardAction[];
  alert: Alert;
  onAlertCardPress?: () => void;
  onContentPress?: () => void;
  onMenuPress?: () => void;
}

// TODO(SRG-276): Remove this simplified card once the rich card ships — item
// thumbnail + inline quantity stepper and tappable support rows that open the
// item/invoice detail drawers (Lost Item → SRG-211, Overdue Payment → SRG-212).
export const AlertCard: React.FC<AlertCardProps> = ({
  actions,
  alert,
  onAlertCardPress,
  onContentPress,
  onMenuPress,
}) => {
  const [styles, theme] = useStyles();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const avatarProps = useMemo(
    () => createAvatarProps(alert.title, alert.restaurantUUID, 1),
    [alert.title, alert.restaurantUUID],
  );

  const delegatesMenu = onMenuPress !== undefined;
  const shouldShowMenuTrigger =
    IS_MOBILE || isHovered || (!delegatesMenu && isMenuOpen);

  const handleAction = (
    event: GestureResponderEvent | undefined,
    onPress: () => void,
  ) => {
    event?.stopPropagation();
    setIsMenuOpen(false);
    onPress();
  };

  const content = (
    <YStack
      backgroundColor={theme.sk.color.bg.primary.default}
      gap={theme.sk.spacing.spaceSmall}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      paddingBottom={0}
      paddingHorizontal={theme.sk.spacing.spaceMedium}
      paddingTop={theme.sk.spacing.spaceMedium}
    >
      <XStack
        alignItems="center"
        justifyContent="space-between"
        marginBottom={theme.sk.spacing.spaceXsmall}
      >
        <AlertPill alertType={alert.cardType} />
        {delegatesMenu ? (
          <Pressable
            accessibilityLabel={i18n.t('more_actions')}
            accessibilityRole="button"
            onPress={(event?: GestureResponderEvent) => {
              event?.stopPropagation();
              onMenuPress();
            }}
            style={
              shouldShowMenuTrigger
                ? styles.moreButton
                : styles.moreButtonHidden
            }
          >
            <Icons.Ellipsis
              color={theme.sk.color.icon.tertiary}
              size={ELLIPSIS_ICON_SIZE}
            />
          </Pressable>
        ) : (
          <DropdownMenu
            onOpenChange={setIsMenuOpen}
            open={isMenuOpen}
            placement="bottom-end"
          >
            <DropdownMenu.Trigger asChild>
              <Pressable
                accessibilityLabel={i18n.t('more_actions')}
                accessibilityRole="button"
                onPress={(event?: GestureResponderEvent) => {
                  event?.stopPropagation();
                  setIsMenuOpen((open) => !open);
                }}
                style={
                  shouldShowMenuTrigger
                    ? styles.moreButton
                    : styles.moreButtonHidden
                }
              >
                <Icons.Ellipsis
                  color={theme.sk.color.icon.tertiary}
                  size={ELLIPSIS_ICON_SIZE}
                />
              </Pressable>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              autoFocusFirstItem={false}
              minWidth={MENU_MIN_WIDTH}
            >
              {actions.map((action) => (
                <DropdownMenu.Item
                  key={action.key}
                  onPress={(event?: GestureResponderEvent) =>
                    handleAction(event, action.onPress)
                  }
                >
                  <Typography size={IS_MOBILE ? '$sm' : '$md'} textAlign="left">
                    {action.label}
                  </Typography>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu>
        )}
      </XStack>
      <XStack alignItems="center" gap={theme.sk.spacing.spaceSMedium}>
        <Avatar {...avatarProps} />
        <YStack flex={1}>
          <Typography
            numberOfLines={1}
            size={IS_MOBILE ? '$md' : '$sm'}
            textAlign="left"
            variant="label"
          >
            {alert.title}
          </Typography>
          <Typography
            color={theme.sk.color.text.tertiary}
            numberOfLines={1}
            size={IS_MOBILE ? '$sm' : '$xs'}
            textAlign="left"
          >
            {alert.subtitle}
          </Typography>
        </YStack>
      </XStack>
      {alert.description ? (
        <Typography muted size="$sm" textAlign="left">
          {alert.description}
        </Typography>
      ) : null}
      <AlertSupportInfo alert={alert} onPress={onContentPress} />
    </YStack>
  );

  return onAlertCardPress ? (
    <Pressable accessibilityRole="button" onPress={onAlertCardPress}>
      {content}
    </Pressable>
  ) : (
    content
  );
};

const useStyles = createStyles((theme) => ({
  moreButton: {
    padding: theme.sk.spacing.spaceXsmall,
  },
  moreButtonHidden: {
    opacity: 0,
    padding: theme.sk.spacing.spaceXsmall,
  },
}));
